import { createTheme } from "@mui/material/styles";
import {
  HOOPER_BLUE,
  HOOPER_BLUE_DARK,
  HOOPER_DARK_GRAY,
  HOOPER_GREEN,
  HOOPER_LIGHT_BLUE,
  HOOPER_NEON_GREEN,
  HOOPER_NEON_GREEN_DARK,
  HOOPER_RED,
  HOOPER_YELLOW,
  OFF_WHITE,
} from "./constants";

export const THEME_MODE_STORAGE_KEY = "admin.theme.mode";
export const COLOR_SCHEME_SELECTOR = "data-mui-color-scheme";

// Alpha overlay ramp. Used for subtle borders and hover tints — roughly 200
// call sites across the app wrote these as literal rgba(0,0,0,X), which is
// wrong in dark mode where the tint must be white.
interface OverlayRamp {
  1: string;
  2: string;
  3: string;
  4: string;
  5: string;
}

// A brand mark that must always read as "Hooper blue", independent of the
// accent (primary.main, which swaps to neon green in dark). HOOPER_BLUE
// itself is tuned for white text/surfaces and measures ~2.96:1 on the dark
// page background — below AA — so dark mode uses HOOPER_LIGHT_BLUE
// (~8.39:1 on the dark page) instead.
interface BrandBluePalette {
  main: string;
}

// Phase 1 deliberately pointed the *semantic* status tokens (error/warning/
// success) at MUI's own defaults in light mode, not the Hooper brand hues —
// HOOPER_RED/YELLOW/GREEN fail contrast on white (see the light error/
// warning/success comment below). But some call sites want the actual brand
// colour to keep rendering (a checkmark/cross that has always been Hooper
// red/green, not MUI's red/green) while still needing to stay legible in
// dark mode. brandRed/brandGreen/brandYellow carry that: the original brand
// hex in light, and the same lightened value already tuned for the dark
// page background as the matching status token in dark.
interface BrandStatusPalette {
  main: string;
}

declare module "@mui/material/styles" {
  interface Palette {
    surfaceMuted: string;
    overlay: OverlayRamp;
    brandBlue: BrandBluePalette;
    brandRed: BrandStatusPalette;
    brandGreen: BrandStatusPalette;
    brandYellow: BrandStatusPalette;
  }
  interface PaletteOptions {
    surfaceMuted?: string;
    overlay?: OverlayRamp;
    brandBlue?: BrandBluePalette;
    brandRed?: BrandStatusPalette;
    brandGreen?: BrandStatusPalette;
    brandYellow?: BrandStatusPalette;
  }
}

const lightOverlay: OverlayRamp = {
  1: "rgba(0, 0, 0, 0.02)",
  2: "rgba(0, 0, 0, 0.04)",
  3: "rgba(0, 0, 0, 0.06)",
  4: "rgba(0, 0, 0, 0.08)",
  5: "rgba(0, 0, 0, 0.1)",
};

const darkOverlay: OverlayRamp = {
  1: "rgba(255, 255, 255, 0.02)",
  2: "rgba(255, 255, 255, 0.04)",
  3: "rgba(255, 255, 255, 0.06)",
  4: "rgba(255, 255, 255, 0.08)",
  5: "rgba(255, 255, 255, 0.1)",
};

const theme = createTheme({
  cssVariables: { colorSchemeSelector: COLOR_SCHEME_SELECTOR },
  colorSchemes: {
    light: {
      palette: {
        primary: {
          main: HOOPER_BLUE,
          dark: HOOPER_BLUE_DARK,
          contrastText: "#ffffff",
        },
        background: { default: "#ffffff", paper: "#ffffff" },
        text: {
          primary: HOOPER_DARK_GRAY,
          secondary: "rgba(0, 0, 0, 0.55)",
          disabled: "rgba(0, 0, 0, 0.38)",
        },
        divider: "rgba(0, 0, 0, 0.08)",
        surfaceMuted: "#f8f9fa",
        overlay: lightOverlay,
        brandBlue: { main: HOOPER_BLUE },
        // Before dark mode, the theme declared no palette at all, so light
        // mode ran on MUI's own defaults. Keep those defaults here rather
        // than the Hooper brand red/yellow/green (HOOPER_RED/HOOPER_YELLOW/
        // HOOPER_GREEN in constants.ts) — the brand colours read below AA
        // contrast on white. HOOPER_RED/YELLOW/GREEN remain available for
        // non-status uses; only the status roles stay on MUI's defaults.
        error: { main: "#d32f2f" },
        warning: { main: "#ed6c02" },
        success: { main: "#2e7d32" },
        // Brand-fixed status colours: for call sites that mean "the actual
        // Hooper red/green/yellow", not "whatever MUI's error/success/
        // warning happen to resolve to". Light keeps the brand hex exactly;
        // dark reuses the same lightened value already tuned for the dark
        // page background as the matching status token below.
        brandRed: { main: HOOPER_RED },
        brandGreen: { main: HOOPER_GREEN },
        brandYellow: { main: HOOPER_YELLOW },
      },
    },
    dark: {
      palette: {
        primary: {
          main: HOOPER_NEON_GREEN,
          dark: HOOPER_NEON_GREEN_DARK,
          // Neon green is too luminous for white text to be legible.
          contrastText: HOOPER_DARK_GRAY,
        },
        background: { default: HOOPER_DARK_GRAY, paper: "#1e2224" },
        text: {
          primary: OFF_WHITE,
          secondary: "rgba(255, 255, 255, 0.6)",
          disabled: "rgba(255, 255, 255, 0.38)",
        },
        divider: "rgba(255, 255, 255, 0.12)",
        surfaceMuted: "#212527",
        overlay: darkOverlay,
        brandBlue: { main: HOOPER_LIGHT_BLUE },
        // Status colours lightened; the light-mode values land near 4:1 on
        // #16191a, which is borderline for small text.
        error: { main: "#ff5c74" },
        warning: { main: "#ffc35c" },
        success: { main: "#3bd96a" },
        // Same lightened values as the matching status token above — see
        // the brandRed/Green/Yellow comment in the light scheme.
        brandRed: { main: "#ff5c74" },
        brandGreen: { main: "#3bd96a" },
        brandYellow: { main: "#ffc35c" },
      },
    },
  },
  typography: {
    fontFamily: [
      "Inter",
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
    ].join(","),
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "var(--mui-palette-background-default)",
          color: "var(--mui-palette-text-primary)",
          minHeight: "100vh",
          margin: 0,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          border: "1px solid var(--mui-palette-divider)",
          borderRadius: 12,
          backgroundColor: "var(--mui-palette-background-paper)",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            backgroundColor: "var(--mui-palette-surfaceMuted)",
            color: "var(--mui-palette-text-secondary)",
            fontWeight: 600,
            fontSize: "0.8rem",
            letterSpacing: "0.02em",
            borderBottom: "1px solid var(--mui-palette-divider)",
            padding: "12px 16px",
            whiteSpace: "nowrap",
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          "& .MuiTableRow-root": {
            transition: "background-color 0.12s ease",
            "&:hover": {
              backgroundColor: "var(--mui-palette-overlay-1)",
            },
            "&:last-child .MuiTableCell-root": {
              borderBottom: "none",
            },
          },
          "& .MuiTableCell-root": {
            borderBottom: "1px solid var(--mui-palette-overlay-3)",
            padding: "14px 16px",
            fontSize: "0.875rem",
            color: "var(--mui-palette-text-primary)",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: "0.75rem",
          borderRadius: 20,
          height: 28,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { boxShadow: "none" },
      },
    },
  },
});

export default theme;
